/**
 * Static front-end catalog placeholder.
 * Image fields are intentionally empty strings: drop in real Sunset Concept Store
 * photography later (put files in src/assets and import, or use /images/*).
 */


export type Product = {
  id: string;
  slug: string;
  name: string;
  price: number;
  category: string;
  isNew?: boolean;
  image?: string;
  hoverImage?: string;
};

/** Taille disponible : `stock` pourra être piloté plus tard par le back-office. */
export type SizeVariant = { size: string; sku: string; stock: number | null };

export type ColorVariant = {
  id: string;
  label: string;
  swatch: string;
  images: string[];
  sizes: SizeVariant[];
};

export type ProductDetail = Product & {
  subcategory: string;
  shortDescription: string;
  details: string[];
  /** Déclinaisons couleur/taille. Absent pour les pièces sans variantes (sacs, accessoires). */
  colors?: ColorVariant[];
  /** Galerie utilisée quand le produit n'a pas de déclinaison couleur. */
  images?: string[];
  /** Lien de la collection parente pour le fil d'Ariane. */
  categoryTo?: string;
  /** Couleur unique affichée quand le produit n'a pas de déclinaison. */
  colorLabel?: string;
  /** Taille unique affichée quand le produit n'a pas de tailles S/M/L. */
  sizeLabel?: string;
  /** Tailles disponibles pour un produit sans déclinaison couleur. */
  sizes?: SizeVariant[];
  /** Visuel 1200x630 dédié aux partages sociaux (og:image / twitter:image). */
  ogImage?: string;
  /** Meta title SEO dédié (< 60 caractères). Sinon "Nom — Sunset Concept Store". */
  seoTitle?: string;
  /** Meta description SEO dédiée (< 160 caractères). Sinon shortDescription tronquée. */
  seoDescription?: string;
  /**
   * Identifiant numérique du produit Shopify officiellement rattaché à cette fiche.
   * Source de vérité du mapping Lovable → Shopify : quand il est renseigné, seul
   * ce produit Shopify peut alimenter la fiche (protection anti-doublon).
   */
  shopifyProductId?: string;
};



const sizesFor = (sku: string): SizeVariant[] => [
  { size: "S", sku: `${sku}-S`, stock: null },
  { size: "M", sku: `${sku}-M`, stock: null },
  { size: "L", sku: `${sku}-L`, stock: null },
];

export const ensembleCapri: ProductDetail = {
  id: "ensemble-capri",
  slug: "ensemble-capri",
  ogImage: "/og/ensemble-capri.jpg",
  seoTitle: "Ensemble Capri 3 pièces 449 DH | SUNSET",
  seoDescription:
    "Ensemble de plage Capri 3 pièces, coupe fluide et tissu léger. 449 DH, livraison partout au Maroc et paiement à la livraison.",
  name: "Ensemble Capri",
  price: 449,
  category: "Beachwear",
  subcategory: "Ensembles plage",
  isNew: true,
  image: "/img/ensemble-sunset-orange-1.webp",
  hoverImage: "/img/ensemble-sunset-orange-2.webp",
  shortDescription:
    "Un ensemble trois pièces pensé pour vos journées au soleil. Un bikini féminin accompagné d'un pantalon de plage transparent et léger.",
  details: [
    "Ensemble 3 pièces",
    "Bikini triangle",
    "Bas assorti",
    "Pantalon transparent",
    "Coupe légère et fluide",
    "Idéal pour la plage, la piscine et les vacances",
  ],
  colors: [
    {
      id: "orange",
      label: "Orange",
      swatch: "#F26B21",
      images: [
        "/img/ensemble-sunset-orange-1.webp",
        "/img/ensemble-sunset-orange-2.webp",
        "/img/ensemble-sunset-orange-3.webp",
      ],
      sizes: sizesFor("CAP-ORANGE"),
    },
    {
      id: "rose",
      label: "Rose",
      swatch: "#F4629A",
      images: [
        "/img/ensemble-sunset-rose-1.webp",
        "/img/ensemble-sunset-rose-2.webp",
        "/img/ensemble-sunset-rose-3.webp",
      ],
      sizes: sizesFor("CAP-ROSE"),
    },
    {
      id: "blanc",
      label: "Blanc",
      swatch: "#F6F1E7",
      images: [
        "/img/ensemble-capri-blanc-1.webp",
        "/img/ensemble-capri-blanc-2.webp",
        "/img/ensemble-capri-blanc-3.webp",
      ],
      sizes: sizesFor("CAP-BLANC"),
    },
  ],
};

export const cabasRiviera: ProductDetail = {
  id: "cabas-riviera-tresse",
  slug: "cabas-riviera-tresse",
  ogImage: "/og/cabas-riviera-tresse.jpg",
  seoTitle: "Cabas Riviera Tressé 399 DH | SUNSET",
  seoDescription:
    "Cabas d'été tressé à la main, format spacieux pour la plage et la ville. 399 DH, livré partout au Maroc, paiement à la livraison.",
  name: "Cabas Riviera Tressé",
  price: 399,
  category: "Sacs",
  subcategory: "Paniers & cabas d'été",
  categoryTo: "/sacs",
  isNew: true,
  image: "/img/cabas-riviera-1.webp",
  hoverImage: "/img/cabas-riviera-3.webp",
  shortDescription:
    "Grand cabas tressé pour femme en beige naturel, avec finitions et anses marron. Spacieux et élégant, idéal pour la plage, les vacances et les sorties d'été.",
  details: [
    "Paille tressée beige naturel",
    "Anses et finitions marron",
    "Bandoulière amovible",
    "Doublure intérieure avec poche zippée",
    "Dimensions : 44 × 30 × 13 cm (anses 20 cm)",
    "Idéal plage, vacances et sorties d'été",
  ],
  images: [
    "/img/cabas-riviera-1.webp",
    "/img/cabas-riviera-2.webp",
    "/img/cabas-riviera-3.webp",
    "/img/cabas-riviera-4.webp",
    "/img/cabas-riviera-5.webp",
  ],
};

export const cabasFlora: ProductDetail = {
  id: "cabas-flora-sequin",
  slug: "cabas-flora-sequin",
  ogImage: "/og/cabas-flora-sequin.jpg",
  seoTitle: "Cabas Flora Sequin 449 DH | SUNSET",
  seoDescription:
    "Panier de plage brodé de sequins, esprit méditerranéen. 449 DH, livraison partout au Maroc et paiement à la livraison.",
  name: "Cabas Flora Sequin",
  price: 449,
  category: "Sacs",
  subcategory: "Paniers & cabas d'été",
  categoryTo: "/sacs",
  isNew: true,
  image: "/img/cabas-flora-2.webp",
  hoverImage: "/img/cabas-flora-1.webp",
  shortDescription:
    "Cabas d'été tressé orné de motifs floraux en sequins et doté d'anses rondes effet rotin. Spacieux et original, il accompagne parfaitement vos journées à la plage, vos vacances et vos sorties estivales.",
  details: [
    "Toile tressée naturelle brodée de sequins floraux",
    "Anses rondes effet rotin",
    "Format spacieux, idéal plage et vacances",
    "Pièce unique aux couleurs estivales",
  ],
  images: [
    "/img/cabas-flora-2.webp",
    "/img/cabas-flora-1.webp",
    "/img/cabas-flora-3.webp",
    "/img/cabas-flora-4.webp",
  ],
};

export const cabasNaturel: ProductDetail = {
  id: "cabas-riviera-naturel",
  slug: "cabas-riviera-naturel",
  ogImage: "/og/cabas-riviera-naturel.jpg",
  seoTitle: "Cabas Riviera Naturel 379 DH | SUNSET",
  seoDescription:
    "Cabas en fibres naturelles, léger et résistant pour l'été. 379 DH, livraison partout au Maroc, paiement à la livraison.",
  name: "Cabas Riviera Naturel",
  price: 379,
  category: "Sacs",
  subcategory: "Paniers & cabas d'été",
  categoryTo: "/sacs",
  isNew: true,
  image: "/img/cabas-naturel-2.webp",
  hoverImage: "/img/cabas-naturel-3.webp",
  shortDescription:
    "Cabas tressé au style naturel avec grandes poignées rondes effet rotin. Spacieux, léger et élégant, idéal pour la plage, les vacances, le shopping et les sorties d'été.",
  details: [
    "Paille tressée ton naturel écru",
    "Grandes poignées rondes effet rotin",
    "Format spacieux et très léger",
    "Dimensions : 38 × 27 × 16 cm",
    "Idéal plage, vacances, shopping et sorties d'été",
  ],
  images: [
    "/img/cabas-naturel-2.webp",
    "/img/cabas-naturel-3.webp",
    "/img/cabas-naturel-1.webp",
    "/img/cabas-naturel-4.webp",
    "/img/cabas-naturel-5.webp",
  ],
};

export const kimonoEmeraude: ProductDetail = {
  id: "kimono-emeraude",
  slug: "kimono-emeraude",
  ogImage: "/og/kimono-emeraude.jpg",
  seoTitle: "Kimono Émeraude Femme 449 DH | SUNSET",
  seoDescription:
    "Kimono de plage émeraude en voile léger, coupe fluide. 449 DH, livraison partout au Maroc et paiement à la livraison.",
  name: "Kimono Émeraude",
  price: 449,
  category: "Kimonos",
  subcategory: "Kimonos & Cover-ups",
  categoryTo: "/kimonos",
  isNew: true,
  colorLabel: "Vert",
  sizeLabel: "Taille unique",
  image: "/img/kimono-emeraude-1.webp",
  hoverImage: "/img/kimono-emeraude-3.webp",
  shortDescription:
    "Kimono long imprimé à la coupe ample et fluide, doté de manches longues et d'une ceinture à nouer à la taille. Léger et agréable à porter, il est idéal sur un maillot de bain pour la plage, la piscine ou les sorties estivales.",
  details: [
    "Kimono long imprimé tie & dye vert",
    "Coupe ample et fluide, manches longues",
    "Ceinture à nouer à la taille",
    "Matière : rayonne légère",
    "Taille unique",
    "Idéal plage, piscine et sorties estivales",
  ],
  images: [
    "/img/kimono-emeraude-1.webp",
    "/img/kimono-emeraude-2.webp",
    "/img/kimono-emeraude-3.webp",
    "/img/kimono-emeraude-4.webp",
  ],
};

export const kimonoPaonBoheme: ProductDetail = {
  id: "kimono-paon-boheme",
  slug: "kimono-paon-boheme",
  ogImage: "/og/kimono-paon-boheme.jpg",
  seoTitle: "Kimono Paon Bohème 479 DH | SUNSET",
  seoDescription:
    "Kimono bohème imprimé paon, idéal en cover-up de plage. 479 DH, livraison partout au Maroc, paiement à la livraison.",
  name: "Kimono Paon Bohème",
  price: 479,
  category: "Kimonos",
  subcategory: "Kimonos & Cover-ups",
  categoryTo: "/kimonos",
  isNew: true,
  colorLabel: "Vert imprimé floral & doré",
  sizes: [
    { size: "M", sku: "KPB-M", stock: null },
    { size: "L", sku: "KPB-L", stock: null },
  ],
  image: "/img/kimono-paon-boheme-1.webp",
  hoverImage: "/img/kimono-paon-boheme-2.webp",
  shortDescription:
    "Kimono long au style bohème, sublimé par un imprimé floral aux nuances vertes et dorées et des motifs inspirés du paon. Sa coupe fluide, ses manches larges et sa ceinture à nouer à la taille lui donnent une allure élégante et estivale. Idéal comme cover-up sur un maillot de bain, pour la plage, la piscine, les vacances ou les sorties d'été.",
  details: [
    "Kimono long bohème à imprimé floral et motifs inspirés du paon",
    "Nuances vertes rehaussées de détails dorés",
    "Coupe fluide, manches larges",
    "Ceinture à nouer à la taille",
    "Tailles disponibles : M et L",
    "Idéal en cover-up sur un maillot de bain",
  ],
  images: [
    "/img/kimono-paon-boheme-1.webp",
    "/img/kimono-paon-boheme-2.webp",
    "/img/kimono-paon-boheme-3.webp",
    "/img/kimono-paon-boheme-4.webp",
  ],
};

export const kimonoBohemePaisley: ProductDetail = {
  id: "kimono-boheme-paisley",
  slug: "kimono-boheme-paisley",
  ogImage: "/og/kimono-boheme-paisley.jpg",
  name: "Kimono Bohème Paisley – Voile Léger",
  price: 399,
  category: "Kimonos",
  subcategory: "Kimonos & Cover-ups",
  categoryTo: "/kimonos",
  isNew: true,
  sizeLabel: "Taille unique",
  image: "/img/kimono-paisley-noir.webp",
  hoverImage: "/img/kimono-paisley-rose.webp",
  shortDescription:
    "Kimono léger et fluide au style bohème, orné d'un imprimé paisley délicat. Sa matière voile aérienne et sa coupe ample en font le cover-up idéal sur un maillot de bain, à la plage comme en terrasse.",
  details: [
    "Kimono long en voile léger",
    "Imprimé paisley bohème",
    "Coupe ample et fluide, manches larges",
    "Ouverture devant, à porter ouvert ou noué",
    "4 coloris : Noir, Rose, Blanc, Bleu",
    "Taille unique",
    "Idéal en cover-up plage, piscine et vacances",
  ],
  colors: [
    {
      id: "noir",
      label: "Noir",
      swatch: "#1C1B1A",
      images: ["/img/kimono-paisley-noir.webp", "/img/kimono-paisley-couleurs.webp"],
      sizes: [],
    },
    {
      id: "rose",
      label: "Rose",
      swatch: "#E9A7B0",
      images: ["/img/kimono-paisley-rose.webp", "/img/kimono-paisley-couleurs.webp"],
      sizes: [],
    },
    {
      id: "blanc",
      label: "Blanc",
      swatch: "#F6F1E7",
      images: ["/img/kimono-paisley-blanc.webp", "/img/kimono-paisley-couleurs.webp"],
      sizes: [],
    },
    {
      id: "bleu",
      label: "Bleu",
      swatch: "#5B7FA6",
      images: ["/img/kimono-paisley-bleu.webp", "/img/kimono-paisley-couleurs.webp"],
      sizes: [],
    },
  ],
};

export const maillotRivieraFloral: ProductDetail = {
  id: "maillot-pareo-riviera-floral",
  slug: "maillot-pareo-riviera-floral",
  ogImage: "/og/maillot-pareo-riviera-floral.jpg",
  seoTitle: "Maillot & Paréo Riviera Floral 429 DH | SUNSET",
  seoDescription:
    "Ensemble maillot de bain et paréo imprimé floral. 429 DH, tailles S à L, livraison partout au Maroc, paiement à la livraison.",
  name: "Ensemble Maillot & Paréo Riviera Floral",
  price: 429,
  category: "Beachwear",
  subcategory: "Maillots de bain",
  categoryTo: "/maillots",
  isNew: true,
  colorLabel: "Floral blanc, rose & bleu turquoise",
  sizes: [
    { size: "M", sku: "MPRF-M", stock: null },
    { size: "L", sku: "MPRF-L", stock: null },
    { size: "XL", sku: "MPRF-XL", stock: null },
  ],
  image: "/img/maillot-riviera-floral-1.webp",
  hoverImage: "/img/maillot-riviera-floral-2.webp",
  shortDescription:
    "Un ensemble estival élégant composé d'un maillot de bain une pièce à imprimé floral et d'un paréo long assorti. Le maillot se distingue par son décolleté en V, son imprimé floral rose sur fond clair et son détail volanté bleu turquoise sur l'épaule. Le paréo assorti reprend le même imprimé floral avec des finitions bleu turquoise et se noue élégamment à la taille. Un ensemble idéal pour la plage, la piscine, les vacances et les séjours en bord de mer.",
  details: [
    "Ensemble 2 pièces : maillot + paréo",
    "Maillot de bain une pièce",
    "Décolleté en V",
    "Imprimé floral",
    "Détail volanté bleu turquoise",
    "Paréo long assorti",
    "Paréo à nouer à la taille",
    "Style chic et estival",
  ],
  images: [
    "/img/maillot-riviera-floral-1.webp",
    "/img/maillot-riviera-floral-2.webp",
    "/img/maillot-riviera-floral-3.webp",
    "/img/maillot-riviera-floral-4.webp",
    "/img/maillot-riviera-floral-5.webp",
  ],
};

export const maillotHazel: ProductDetail = {
  id: "ensemble-hazel-maillot-pareo",
  slug: "ensemble-hazel-maillot-pareo",
  ogImage: "/og/ensemble-hazel-maillot-pareo.jpg",
  seoTitle: "Ensemble Hazel Maillot & Paréo 449 DH | SUNSET",
  seoDescription:
    "Maillot de bain et paréo assortis Hazel, silhouette élégante. 449 DH, livraison partout au Maroc et paiement à la livraison.",
  name: "Ensemble Hazel – Maillot & Paréo",
  price: 449,
  category: "Beachwear",
  subcategory: "Maillots de bain",
  categoryTo: "/maillots",
  isNew: true,
  colorLabel: "Terracotta & imprimé graphique",
  sizes: [
    { size: "S", sku: "EHMP-S", stock: null },
    { size: "M", sku: "EHMP-M", stock: null },
    { size: "L", sku: "EHMP-L", stock: null },
    { size: "XL", sku: "EHMP-XL", stock: null },
  ],
  image: "/img/maillot-hazel-1.webp",
  hoverImage: "/img/maillot-hazel-2.webp",
  shortDescription:
    "Un ensemble de plage élégant composé d'un maillot de bain deux pièces asymétrique et d'un paréo assorti à imprimé graphique. Le haut présente une coupe asymétrique drapée, tandis que le bas taille haute offre une silhouette élégante et confortable. Le paréo imprimé complète parfaitement l'ensemble pour créer un look chic et estival, idéal pour la plage, la piscine, les vacances et les journées en resort.",
  details: [
    "Ensemble 3 pièces : haut, bas et paréo",
    "Haut de maillot asymétrique drapé",
    "Bas de maillot taille haute",
    "Paréo imprimé assorti à nouer",
    "Imprimé graphique terracotta et ivoire",
    "Tailles disponibles : S, M, L, XL",
    "Style chic, estival, resort wear",
  ],
  images: [
    "/img/maillot-hazel-1.webp",
    "/img/maillot-hazel-2.webp",
    "/img/maillot-hazel-3.webp",
    "/img/maillot-hazel-4.webp",
    "/img/maillot-hazel-5.webp",
    "/img/maillot-hazel-6.webp",
  ],
};

export const maillotTropic: ProductDetail = {
  id: "ensemble-tropic-maillot-pareo",
  slug: "ensemble-tropic-maillot-pareo",
  ogImage: "/og/ensemble-tropic-maillot-pareo.jpg",
  seoTitle: "Ensemble Tropic Maillot & Paréo 429 DH | SUNSET",
  seoDescription:
    "Maillot de bain et paréo Tropic aux imprimés solaires. 429 DH, livraison partout au Maroc, paiement à la livraison.",
  name: "Ensemble Tropic – Maillot & Paréo",
  price: 429,
  category: "Beachwear",
  subcategory: "Maillots de bain",
  categoryTo: "/maillots",
  isNew: true,
  colorLabel: "Beige, vert, orange & imprimé tropical",
  sizes: [
    { size: "S", sku: "ETMP-S", stock: null },
    { size: "M", sku: "ETMP-M", stock: null },
    { size: "L", sku: "ETMP-L", stock: null },
    { size: "XL", sku: "ETMP-XL", stock: null },
  ],
  image: "/img/maillot-tropic-1.webp",
  hoverImage: "/img/maillot-tropic-2.webp",
  shortDescription:
    "Un ensemble estival élégant composé d'un maillot de bain une pièce à imprimé tropical et d'un paréo long assorti. Le maillot présente une coupe croisée avec décolleté en V et un imprimé aux tons beige, vert, orange et floral. Le paréo assorti reprend le même imprimé tropical avec une ceinture vert sauge à nouer à la taille. Un ensemble chic et confortable, idéal pour la plage, la piscine, les vacances et les journées en bord de mer.",
  details: [
    "Ensemble 2 pièces",
    "Maillot de bain une pièce",
    "Décolleté en V",
    "Coupe croisée",
    "Imprimé tropical",
    "Paréo long assorti",
    "Ceinture à nouer à la taille",
    "Style resort / vacances / été",
  ],
  images: [
    "/img/maillot-tropic-1.webp",
    "/img/maillot-tropic-2.webp",
    "/img/maillot-tropic-3.webp",
  ],
};

export const braceletsBoheme: ProductDetail = {
  id: "set-7-bracelets-boheme",
  slug: "set-7-bracelets-boheme",
  ogImage: "/og/set-7-bracelets-boheme.jpg",
  seoTitle: "Set 7 Bracelets Bohème Coquillages 199 DH | SUNSET",
  seoDescription:
    "Set de 7 bracelets bohèmes coquillages et étoile de mer. 199 DH, livraison partout au Maroc et paiement à la livraison.",
  name: "Set de 7 Bracelets Bohème – Coquillages & Étoile de Mer",
  price: 199,
  category: "Bijoux",
  subcategory: "Bijoux & Accessoires",
  categoryTo: "/accessoires",
  isNew: true,
  colorLabel: "Blanc / Crème / Marron / Doré",
  sizeLabel: "Taille unique",
  image: "/img/bracelets-boheme-1.webp",
  hoverImage: "/img/bracelets-boheme-2.webp",
  shortDescription:
    "Apportez une touche bohème et estivale à vos looks avec ce set de 7 bracelets inspiré de l'univers de la plage. Le set associe des perles blanches, crème et marron, des détails dorés, des coquillages ainsi qu'un pendentif étoile de mer. Les 7 bracelets peuvent être portés ensemble pour créer un look bohème complet ou séparément selon le style souhaité. Parfait pour les vacances, la plage, les journées d'été et les tenues beachwear.",
  details: [
    "Set de 7 pièces",
    "Style bohème et estival",
    "Perles décoratives",
    "Coquillages",
    "Détails dorés",
    "Pendentif étoile de mer",
    "Peut être porté ensemble ou séparément",
    "Idéal pour les looks plage et vacances",
  ],
  images: [
    "/img/bracelets-boheme-1.webp",
    "/img/bracelets-boheme-2.webp",
    "/img/bracelets-boheme-3.webp",
    "/img/bracelets-boheme-4.webp",
  ],
};

export const foulardBandanaPaisley: ProductDetail = {
  id: "foulard-bandana-paisley",
  slug: "foulard-bandana-paisley",
  ogImage: "/og/foulard-bandana-paisley.jpg",
  // Produit Shopify officiel (5 variantes + stock). L'ancien doublon 8047492825136
  // est ignoré par la résolution catalogue.
  shopifyProductId: "8047494496304",
  name: "Foulard Bandana Paisley",
  price: 199,
  category: "Foulards",
  subcategory: "Foulards & Accessoires",
  categoryTo: "/accessoires",
  isNew: true,
  sizeLabel: "Taille unique",
  image: "/img/foulard-paisley-bleu-marine.webp",
  hoverImage: "/img/foulard-paisley-couleurs.webp",
  seoTitle: "Foulard Bandana Paisley 199 DH | Sunset Concept Store",
  seoDescription:
    "Foulard bandana paisley femme, 5 coloris, taille unique. 199 DH, livraison gratuite au Maroc et paiement à la livraison.",
  shortDescription:
    "Le foulard Bandana Paisley est l'accessoire parfait pour compléter vos looks d'été. Léger, élégant et facile à porter, il peut être noué autour du cou, porté dans les cheveux, sur la tête ou utilisé pour accessoiriser un sac.",
  details: [
    "Style bohème chic / été",
    "Motif Paisley",
    "Taille unique",
    "5 coloris disponibles",
    "Livraison gratuite partout au Maroc",
    "Paiement à la livraison",
  ],
  colors: [
    {
      id: "bleu-marine-blanc",
      label: "Bleu marine / Blanc",
      swatch: "#1F3A63",
      images: ["/img/foulard-paisley-bleu-marine.webp", "/img/foulard-paisley-couleurs.webp"],
      sizes: [],
    },
    {
      id: "vert-blanc",
      label: "Vert / Blanc",
      swatch: "#6E7F5C",
      images: ["/img/foulard-paisley-vert.webp", "/img/foulard-paisley-couleurs.webp"],
      sizes: [],
    },
    {
      id: "bleu-ciel-blanc",
      label: "Bleu ciel / Blanc",
      swatch: "#8FB4D4",
      images: ["/img/foulard-paisley-bleu-ciel.webp", "/img/foulard-paisley-couleurs.webp"],
      sizes: [],
    },
    {
      id: "marron-beige",
      label: "Marron / Beige",
      swatch: "#B08A62",
      images: ["/img/foulard-paisley-beige.webp", "/img/foulard-paisley-couleurs.webp"],
      sizes: [],
    },
    {
      id: "gris-noir",
      label: "Gris / Noir",
      swatch: "#6B6B6B",
      images: ["/img/foulard-paisley-gris.webp", "/img/foulard-paisley-couleurs.webp"],
      sizes: [],
    },
  ],
};

export const foulardCasquetteBandanaPaisley: ProductDetail = {
  id: "foulard-casquette-bandana-paisley",
  slug: "foulard-casquette-bandana-paisley",
  ogImage: "/og/foulard-casquette-bandana-paisley.jpg",
  // Produit Shopify officiel (4 variantes couleur, suivi de stock actif).
  shopifyProductId: "8047861989424",
  name: "Foulard Casquette Bandana Paisley",
  price: 199,
  category: "Foulards",
  subcategory: "Foulards & Accessoires",
  categoryTo: "/accessoires",
  isNew: true,
  sizeLabel: "Taille unique",
  image: "/img/foulard-casquette-noir.webp",
  hoverImage: "/img/foulard-casquette-blanc.webp",
  seoTitle: "Foulard Casquette Bandana Paisley 199 DH | SUNSET",
  seoDescription:
    "Foulard casquette bandana paisley à visière intégrée, 4 coloris, taille unique. 199 DH, paiement à la livraison partout au Maroc.",
  shortDescription:
    "Un foulard bandana au motif paisley avec visière intégrée, pensé pour apporter une touche élégante et décontractée aux looks d'été. Léger et facile à porter, il associe le style d'un bandana à la protection d'une petite visière.",
  details: [
    "Motif paisley",
    "Visière intégrée",
    "Taille unique",
    "4 coloris disponibles",
    "Paiement à la livraison partout au Maroc",
  ],
  colors: [
    {
      id: "blanc",
      label: "Blanc",
      swatch: "#F6F1E7",
      images: ["/img/foulard-casquette-blanc.webp", "/img/foulard-casquette-blanc-2.webp"],
      sizes: [],
    },
    {
      id: "bleu",
      label: "Bleu",
      swatch: "#2A56B8",
      images: ["/img/foulard-casquette-bleu.webp", "/img/foulard-casquette-blanc-2.webp"],
      sizes: [],
    },
    {
      id: "rouge",
      label: "Rouge",
      swatch: "#C1272D",
      images: ["/img/foulard-casquette-rouge.webp", "/img/foulard-casquette-blanc-2.webp"],
      sizes: [],
    },
    {
      id: "noir",
      label: "Noir",
      swatch: "#1C1B1A",
      images: ["/img/foulard-casquette-noir.webp", "/img/foulard-casquette-blanc-2.webp"],
      sizes: [],
    },
  ],
};

export const robeCrochetRiviera: ProductDetail = {
  id: "robe-crochet-riviera",
  slug: "robe-crochet-riviera",
  ogImage: "/og/robe-crochet-riviera.jpg",
  seoTitle: "Robe Crochet Riviera Femme 499 DH | SUNSET",
  seoDescription:
    "Robe de plage en crochet Riviera, 5 coloris, tailles S à L. 499 DH, livraison partout au Maroc, paiement à la livraison.",
  // Produit Shopify officiel (5 couleurs × 3 tailles).
  shopifyProductId: "8047523692592",
  name: "Robe Crochet Riviera",
  price: 499,
  category: "Beachwear",
  subcategory: "Cover-ups / Robes de plage",
  categoryTo: "/beachwear",
  isNew: true,
  image: "/img/robe-riviera-white-1.webp",
  hoverImage: "/img/robe-riviera-black-1.webp",
  shortDescription:
    "Robe longue de plage en maille crochet ajourée, fluide et élégante, idéale à porter par-dessus un maillot de bain. Sa coupe longue près du corps, son décolleté plongeant et son dos nu apportent un style féminin et estival, parfait pour la plage, la piscine, les vacances ou les beach clubs.",
  details: [
    "Robe longue de plage",
    "Maille crochet ajourée",
    "Coupe près du corps",
    "Décolleté plongeant",
    "Dos nu",
    "Bretelles tour de cou",
    "Tissu léger et respirant",
    "Style beachwear / vacances / été",
    "À porter par-dessus un maillot de bain",
    "Livraison gratuite partout au Maroc",
    "Paiement à la livraison",
  ],
  colors: [
    {
      id: "brown",
      label: "Brown",
      swatch: "#B99372",
      images: ["/img/robe-riviera-brown-1.webp", "/img/robe-riviera-brown-2.webp"],
      sizes: sizesFor("RCR-BROWN"),
    },
    {
      id: "pink",
      label: "Pink",
      swatch: "#F56AAE",
      images: ["/img/robe-riviera-pink-1.webp", "/img/robe-riviera-pink-2.webp"],
      sizes: sizesFor("RCR-PINK"),
    },
    {
      id: "white",
      label: "White",
      swatch: "#F7F4EE",
      images: ["/img/robe-riviera-white-1.webp", "/img/robe-riviera-white-2.webp"],
      sizes: sizesFor("RCR-WHITE"),
    },
    {
      id: "black",
      label: "Black",
      swatch: "#1C1B1A",
      images: ["/img/robe-riviera-black-1.webp", "/img/robe-riviera-black-2.webp"],
      sizes: sizesFor("RCR-BLACK"),
    },
    {
      id: "yellow",
      label: "Yellow",
      swatch: "#F2CE3B",
      images: ["/img/robe-riviera-yellow-1.webp", "/img/robe-riviera-yellow-2.webp"],
      sizes: sizesFor("RCR-YELLOW"),
    },
  ],
};

export const jupeCrochetPlageBlanche: ProductDetail = {
  id: "jupe-longue-crochet-plage-blanche",
  slug: "jupe-longue-crochet-plage-blanche",
  ogImage: "/og/jupe-longue-crochet-plage-blanche.jpg",
  seoTitle: "Jupe Longue Crochet Plage Blanche 199 DH | SUNSET",
  seoDescription:
    "Jupe longue de plage en crochet blanc, tailles S à XL. 199 DH, livraison partout au Maroc et paiement à la livraison.",
  // Produit Shopify officiel (1 couleur × 4 tailles).
  shopifyProductId: "8047535849520",
  name: "Jupe Longue Crochet Plage Blanche",
  price: 199,
  category: "Beachwear",
  subcategory: "Cover-ups / Jupes de plage",
  categoryTo: "/beachwear",
  isNew: true,
  image: "/img/jupe-crochet-blanche-1.webp",
  hoverImage: "/img/jupe-crochet-blanche-2.webp",
  shortDescription:
    "Jupe longue en crochet ajouré, légère et élégante, idéale à porter par-dessus un maillot de bain. Sa coupe longue et fluide apporte une allure chic et décontractée, parfaite pour la plage, la piscine ou les journées d'été.",
  details: [
    "Jupe longue de plage",
    "Maille crochet ajourée",
    "Coupe longue et fluide",
    "Taille élastiquée avec cordon",
    "Tissu léger et respirant",
    "À porter par-dessus un maillot de bain",
    "Livraison gratuite partout au Maroc",
    "Paiement à la livraison",
  ],
  colors: [
    {
      id: "blanc",
      label: "Blanc",
      swatch: "#F7F4EE",
      images: [
        "/img/jupe-crochet-blanche-1.webp",
        "/img/jupe-crochet-blanche-2.webp",
        "/img/jupe-crochet-blanche-3.webp",
        "/img/jupe-crochet-blanche-4.webp",
      ],
      sizes: [
        { size: "S", sku: "JCP-BLANC-S", stock: null },
        { size: "M", sku: "JCP-BLANC-M", stock: null },
        { size: "L", sku: "JCP-BLANC-L", stock: null },
        { size: "XL", sku: "JCP-BLANC-XL", stock: null },
      ],
    },
  ],
};

export const combishortCrochetRaye: ProductDetail = {
  id: "combishort-crochet-raye-noir-blanc",
  slug: "combishort-crochet-raye-noir-blanc",
  seoTitle: "Combishort Crochet Rayé Noir & Blanc 349 DH | SUNSET",
  seoDescription:
    "Combishort en crochet rayé noir et blanc, dos-nu, tailles S à XL. 349 DH, livraison partout au Maroc et paiement à la livraison.",
  // Produit Shopify officiel (1 couleur × 4 tailles).
  shopifyProductId: "8047951675440",
  name: "Combishort Crochet Rayé Noir & Blanc",
  price: 349,
  category: "Beachwear",
  subcategory: "Combishorts",
  categoryTo: "/beachwear",
  isNew: true,
  image: "/img/combishort-crochet-raye-1.webp",
  hoverImage: "/img/combishort-crochet-raye-2.webp",
  shortDescription:
    "Une pièce estivale au style affirmé, pensée pour accompagner vos journées ensoleillées. Son crochet rayé noir et blanc, sa coupe courte et son décolleté dos-nu apportent une silhouette élégante et décontractée, parfaite pour la plage, les vacances ou vos looks d'été.",
  details: [
    "Maille crochet ajourée",
    "Motif rayé noir et blanc",
    "Coupe combishort",
    "Décolleté plongeant",
    "Dos-nu",
    "Style beachwear",
    "Léger et agréable à porter",
    "Disponible du S au XL",
  ],
  colors: [
    {
      id: "noir-blanc",
      label: "Noir & Blanc",
      swatch: "#1C1B1A",
      images: [
        "/img/combishort-crochet-raye-1.webp",
        "/img/combishort-crochet-raye-2.webp",
        "/img/combishort-crochet-raye-3.webp",
        "/img/combishort-crochet-raye-4.webp",
      ],
      sizes: [
        { size: "S", sku: "CCRNB-S", stock: null },
        { size: "M", sku: "CCRNB-M", stock: null },
        { size: "L", sku: "CCRNB-L", stock: null },
        { size: "XL", sku: "CCRNB-XL", stock: null },
      ],
    },
  ],
};


export const productDetails: Record<string, ProductDetail> = {
  [ensembleCapri.slug]: ensembleCapri,
  [cabasRiviera.slug]: cabasRiviera,
  [cabasFlora.slug]: cabasFlora,
  [cabasNaturel.slug]: cabasNaturel,
  [kimonoEmeraude.slug]: kimonoEmeraude,
  [kimonoPaonBoheme.slug]: kimonoPaonBoheme,
  [kimonoBohemePaisley.slug]: kimonoBohemePaisley,
  [maillotRivieraFloral.slug]: maillotRivieraFloral,
  [maillotHazel.slug]: maillotHazel,
  [maillotTropic.slug]: maillotTropic,
  [braceletsBoheme.slug]: braceletsBoheme,
  [foulardBandanaPaisley.slug]: foulardBandanaPaisley,
  [foulardCasquetteBandanaPaisley.slug]: foulardCasquetteBandanaPaisley,
  [robeCrochetRiviera.slug]: robeCrochetRiviera,
  [jupeCrochetPlageBlanche.slug]: jupeCrochetPlageBlanche,
  [combishortCrochetRaye.slug]: combishortCrochetRaye,
};


/** Produits de la collection Maillots de bain. */
export const swimwearProducts: Product[] = [maillotTropic, maillotHazel, maillotRivieraFloral];


export const newArrivals: Product[] = [
  combishortCrochetRaye,
  foulardCasquetteBandanaPaisley,
  jupeCrochetPlageBlanche,
  robeCrochetRiviera,
  foulardBandanaPaisley,
  braceletsBoheme,
  maillotTropic,
  maillotHazel,
  maillotRivieraFloral,

  ensembleCapri,
  cabasRiviera,
  cabasFlora,
  cabasNaturel,
  kimonoEmeraude,
  kimonoPaonBoheme,
  kimonoBohemePaisley,


  {
    id: "ensemble-sunset",
    slug: "ensemble-sunset",
    name: "Ensemble Sunset",
    price: 599,
    category: "Beachwear",
    isNew: true,
  },
  { id: "sac-riviera", slug: "sac-riviera", name: "Sac Riviera", price: 349, category: "Sacs" },
];

export const lookProducts: Product[] = [
  { id: "maillot-amalfi", slug: "maillot-amalfi", name: "Maillot Amalfi", price: 389, category: "Swimwear" },
  { id: "kimono-lin-sable", slug: "kimono-lin-sable", name: "Kimono Lin Sable", price: 479, category: "Kimonos" },
  { id: "panier-essaouira", slug: "panier-essaouira", name: "Panier Essaouira", price: 299, category: "Sacs" },
];

export const accessories: Product[] = [
  foulardCasquetteBandanaPaisley,
  foulardBandanaPaisley,
  braceletsBoheme,
  { id: "foulard-soie-dune", slug: "foulard-soie-dune", name: "Foulard Soie Dune", price: 249, category: "Foulards" },
  { id: "boucles-solaire", slug: "boucles-solaire", name: "Boucles Solaire", price: 179, category: "Bijoux" },
  { id: "sac-medina", slug: "sac-medina", name: "Sac Medina", price: 429, category: "Sacs" },
  { id: "chapeau-tanger", slug: "chapeau-tanger", name: "Chapeau Tanger", price: 219, category: "Accessoires" },
];

export const collections = [
  { title: "Beachwear", to: "/beachwear", caption: "Maillots & pièces solaires" },
  { title: "Maillots de bain", to: "/maillots", caption: "Bikinis & une-pièces" },
  { title: "Kimonos & Cover-ups", to: "/kimonos", caption: "Voiles et lin léger" },
  { title: "Sacs", to: "/sacs", caption: "Paniers & cabas d'été" },
  { title: "Bijoux & Accessoires", to: "/accessoires", caption: "Les détails précieux" },
  { title: "Foulards", to: "/accessoires", caption: "Soie et imprimés" },
] as const;

export const navLinks = [
  { label: "Nouveautés", to: "/nouveautes" },
  { label: "Kimonos", to: "/kimonos" },
  { label: "Sacs", to: "/sacs" },
  {
    label: "Accessoires",
    to: "/accessoires",
    children: [
      { label: "Bijoux", to: "/accessoires" },
      { label: "Foulards", to: "/accessoires" },
    ],
  },
  { label: "Beachwear", to: "/beachwear" },
  { label: "Maillots de bain", to: "/maillots" },
] as const;

